import React, { useEffect, useState } from "react";

import { RefreshCwIcon, UserIcon, UsersIcon } from "@lucide/icons";

import { MicrosoftIcon } from "simplydo/react/ui/brand-icon";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Table, TableBody, TableCell, TableRow } from "simplydo/react/ui/table";

import api from "api";

import type { PowerbiWorkspace, PowerbiWorkspaceChooserProps } from "./types";

function PowerbiWorkspaceChooser({
  user,
  instruction,
  open,
  onComplete,
  onClose,
}: PowerbiWorkspaceChooserProps) {
  const [didLoad, setDidLoad] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [workspaces, setWorkspaces] = useState<PowerbiWorkspace[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const orgId = user?.ownerOrganisation?._id;

  useEffect(() => {
    setDidLoad(false);
    setWorkspaces([]);
  }, []);

  useEffect(() => {
    if (didLoad || !open || !orgId) {
      return;
    }
    setDidLoad(true);
    setUpdating(true);
    api.organisations.getPowerbiWorkspaces(
      orgId,
      (data) => {
        if (data.error) {
          setErrorMessage(data.error);
        } else {
          setErrorMessage("");
        }
        setWorkspaces(
          (data.workspaces as { id?: string; name?: string }[]).map((g) => ({
            id: g.id ?? "",
            name: g.name ?? "",
          })),
        );
        setUpdating(false);
      },
      () => setUpdating(false),
    );
  }, [orgId, open, didLoad]);

  return (
    <React.Fragment>
      <Modal
        open={open}
        dimmer="inverted"
        size="small"
        style={{ zIndex: 100 }}
        closeOnDocumentClick={false}
      >
        <ModalHeader>
          <MicrosoftIcon /> Your workspaces
        </ModalHeader>
        <ModalContent>
          <p>{instruction}</p>
          {updating && <Loader />}
          {errorMessage && !updating ? (
            <Message color="red">
              <MessageHeader>
                We are having trouble connecting to Microsoft
              </MessageHeader>
              <MessageContent>{errorMessage}</MessageContent>
            </Message>
          ) : (
            <Table>
              <TableBody>
                {workspaces.length > 0 ? (
                  <>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>Select one of your group workspaces</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                    {workspaces.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell collapsing>
                          <Icon icon={UsersIcon} />
                        </TableCell>
                        <TableCell>{g.name}</TableCell>
                        <TableCell collapsing>
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onComplete(g);
                            }}
                            color="primary"
                          >
                            Choose
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>Or use your personal workspace</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </>
                ) : null}
                <TableRow>
                  <TableCell>
                    <Icon icon={UserIcon} />
                  </TableCell>
                  <TableCell>
                    {user?.profile?.firstName}
                    &apos;s Workspace
                  </TableCell>
                  <TableCell collapsing>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onComplete(null);
                      }}
                      color="primary"
                    >
                      Choose
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </ModalContent>
        <ModalActions>
          {errorMessage ? (
            <Button onClick={() => setDidLoad(false)}>
              <Icon icon={RefreshCwIcon} />
              Retry loading workspaces
            </Button>
          ) : null}
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
        </ModalActions>
      </Modal>
    </React.Fragment>
  );
}

export default PowerbiWorkspaceChooser;
